/**
 * Lead Distribution — top-level sidebar group for lead-flow reporting.
 *
 * Where a lead GOES and how fast, as opposed to what an agent did with it
 * (that's Whatsapp Lead). Created 2026-08-29 for Ameen's reporting asks.
 *
 *   - Lead TAT   ?tab=tat   lead create -> allocate -> first dial, + the
 *                           connect-rate-vs-speed and arrival-vs-dial shape
 *   - APR                    agent daily performance: dials, connects and the
 *                           shift split into talk / dial / wrap / wait
 *   - RTM                    real-time monitoring: every retail agent's live
 *                           dialling state, idle-first, for a dedicated watcher
 *   - Lead Report            full per-lead master sheet — campaign, dial/connect
 *                           cadence, ownership + reallocation history, NI,
 *                           conversion. ADMIN-ONLY (role 1), unlike the tabs above.
 *
 * Visibility: role 1 (owner), role 17 (Ameen / retail super-manager), bulk WA
 * manager, and floor managers/TLs (13/14) who run the dialling teams. Plus a
 * couple of individually-granted phone overrides scoped to ONE tab each
 * (e.g. 8130433974 -> Campaign Agents only, added 2026-09-10) — these don't
 * widen isSupervisor/isFloorManager, so they never unlock the other tabs.
 */
import React from "react";
import { FormattedMessage } from "react-intl";
import { IconRoute, IconClock, IconActivityHeartbeat, IconReportAnalytics, IconUsers, IconTable } from "@tabler/icons";
import { getDecryptDataFromLocal } from "../../utils/encrypt";

const icons = { IconRoute, IconClock, IconActivityHeartbeat, IconReportAnalytics, IconUsers, IconTable };

const AMEEN_ID = "63ddfd41a136bb63d35bf21b";
// Bulk-team viewers for Campaign Agents (2026-09-09) — Manjay Jha (bulk lead)
// and Ravi Shankar Kumar Singh (his manager), locked to bulk-only data
// server-side (campaignAgentReport.js BULK_VIEWER_IDS). Explicit ids, not the
// broader bulkWaManager flag below — Ravi isn't a bulkWaManager, and this
// report is intentionally two named people, not every bulk manager.
const BULK_CAMPAIGN_AGENTS_VIEWER_IDS = ["620e24cb40f6563d6cfd27df", "61a5c97e996886bb19ee8cda"];
// Individually-granted full-access phone override (2026-09-10) — matches
// routes.js CAMPAIGN_AGENTS_EXTRA_PHONES, which is what actually grants the
// underlying data access; this just surfaces the sidebar item for them.
const CAMPAIGN_AGENTS_EXTRA_PHONES = ["8130433974"];
// Lead Report for a non-admin, retail team only (2026-10-09) — matches
// routes.js LEAD_REPORT_RETAIL_PHONES, which forces team=retail server-side.
const LEAD_REPORT_RETAIL_PHONES = ["9268096291", "9625366141"];
// Lead Report bulk-team-only (2026-10-15) — Manjay Jha; matches routes.js LEAD_REPORT_BULK_IDS.
const LEAD_REPORT_BULK_IDS = ["620e24cb40f6563d6cfd27df"];

const role            = Number(getDecryptDataFromLocal("setRole"));
const adminId         = (() => { try { return localStorage.getItem("adminId") || ""; } catch (e) { return ""; } })();
const phoneNumber     = getDecryptDataFromLocal("phoneNumber");
const isAmeen         = adminId === AMEEN_ID;
const isBulkWaManager = (() => { try { return localStorage.getItem("bulkWaManager") === "true"; } catch (e) { return false; } })();
const isBulkCampaignAgentsViewer = BULK_CAMPAIGN_AGENTS_VIEWER_IDS.includes(adminId);
const isCampaignAgentsExtraPhone = CAMPAIGN_AGENTS_EXTRA_PHONES.includes(phoneNumber);
const isLeadReportRetailPhone = LEAD_REPORT_RETAIL_PHONES.includes(phoneNumber);
const isLeadReportBulkId = LEAD_REPORT_BULK_IDS.includes(adminId);
const isSupervisor    = role === 1 || role === 17 || isBulkWaManager;
const isFloorManager  = role === 13 || role === 14;

const children = [];

// Campaign Agents — tighter than the other tabs on purpose: owner + Ameen
// (retail) or Manjay/Ravi (bulk, 2026-09-09), or an individually-granted
// phone (2026-09-10), only — since it's also where campaign moves happen.
// Each sees only their own team server-side.
if (role === 1 || isAmeen || isBulkCampaignAgentsViewer || isCampaignAgentsExtraPhone) {
  children.push({
    id: "ld-campaign-agents",
    title: <FormattedMessage id="Campaign Agents" defaultMessage="Campaign Agents" />,
    type: "item",
    url: "/admin/lead-distribution/campaign-agents",
    icon: icons.IconUsers,
    breadcrumbs: false,
  });
}

if (isSupervisor || isFloorManager) {
  children.push({
    id: "ld-lead-tat",
    title: <FormattedMessage id="Lead TAT" defaultMessage="Lead TAT" />,
    type: "item",
    url: "/admin/lead-distribution/tat",
    icon: icons.IconClock,
    breadcrumbs: false,
  });
}

if (isSupervisor || isFloorManager) {
  children.push({
    id: "ld-apr",
    title: <FormattedMessage id="APR (Agent Daily)" defaultMessage="APR (Agent Daily)" />,
    type: "item",
    url: "/admin/lead-distribution/apr",
    icon: icons.IconReportAnalytics,
    breadcrumbs: false,
  });
}

if (isSupervisor || isFloorManager) {
  children.push({
    id: "ld-rtm",
    title: <FormattedMessage id="RTM" defaultMessage="RTM" />,
    type: "item",
    url: "/admin/lead-distribution/rtm",
    icon: icons.IconActivityHeartbeat,
    breadcrumbs: false,
  });
}

// Lead Report — full per-lead master sheet. ADMIN-ONLY (role 1), tighter than
// every tab above: Shailendra 2026-09-05 asked for this restricted to admins.
// Plus the retail-only phone override (2026-10-09).
if (role === 1 || isLeadReportRetailPhone || isLeadReportBulkId) {
  children.push({
    id: "ld-lead-report",
    title: <FormattedMessage id="Lead Report" defaultMessage="Lead Report" />,
    type: "item",
    url: "/admin/lead-distribution/lead-report",
    icon: icons.IconTable,
    breadcrumbs: false,
  });
}

export const LeadDistributionTab = {
  id: "lead-distribution",
  title: <FormattedMessage id="Lead Distribution" defaultMessage="Lead Distribution" />,
  type: "group",
  children: children.length > 0 ? [
    {
      id: "ld-group-root",
      title: <FormattedMessage id="Lead Distribution" defaultMessage="Lead Distribution" />,
      type: "collapse",
      icon: icons.IconRoute,
      children,
    },
  ] : [],
};

export const SHOULD_SHOW_LEAD_DISTRIBUTION = isSupervisor || isFloorManager || isCampaignAgentsExtraPhone || isLeadReportRetailPhone || isLeadReportBulkId;
